<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

import { Icon, PlatformLogo, resolveStaticPlatformLogo, type PlatformLogoProps } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    tabs: string[]
    ariaLabel: string
    /** Copy per tab identity. The host resolves it; see `service/channelLabels`. */
    labels?: Record<string, string>
  }>(),
  { labels: () => ({}) },
)
const selected = defineModel<string>({ default: '' })
const buttons = ref<HTMLButtonElement[]>([])

watch(
  () => [selected.value, props.tabs] as const,
  () => {
    if (!props.tabs.includes(selected.value)) selected.value = props.tabs[0] ?? ''
  },
  { immediate: true, deep: true },
)

/**
 * A tab identity with no supplied copy renders as ITSELF. It used to be title-cased here, which
 * manufactured user-facing text out of a machine key — the one thing `layoutConfig.types.ts:9-14`
 * forbids (ORP-20215). Showing the raw identity is honest and visible; a fabricated label reads
 * like real copy and hides the missing key.
 */
function label(tab: string): string {
  return props.labels[tab] ?? tab
}

function logoProps(tab: string): PlatformLogoProps {
  console.log(tab)
  const src = resolveStaticPlatformLogo(tab)
  console.log(src)
  return {
    platform: tab,
    alt: label(tab),
    showText: true,
    size: 25,
    ...(src ? { src } : {}),
  }
}

function isOverview(tab: string): boolean {
  return tab.toLowerCase() === 'overall' || tab.toLowerCase() === 'overview'
}

async function move(index: number, direction: 1 | -1): Promise<void> {
  if (!props.tabs.length) return
  const next = (index + direction + props.tabs.length) % props.tabs.length
  selected.value = props.tabs[next] ?? ''
  await nextTick()
  buttons.value[next]?.focus({ preventScroll: true })
}
</script>

<template>
  <div
    class="report-channel-tabs flex flex-[0_0_4rem] self-stretch bg-surface-sunken p-xs"
    role="tablist"
    :aria-label="ariaLabel"
    aria-orientation="vertical"
  >
    <div class="report-channel-tabs__controls sticky top-xs flex h-fit flex-col gap-xs">
      <button
        v-for="(tab, index) in tabs"
        :key="tab"
        :ref="(element) => element && (buttons[index] = element as HTMLButtonElement)"
        class="report-channel-tabs__tab grid w-11 h-11 place-items-center overflow-hidden p-0 text-[color:var(--ui-color-primary-muted)] bg-transparent rounded-sm cursor-pointer [&:hover]:text-primary [&:hover]:bg-surface-sunken [&:focus-visible]:outline-[length:var(--ui-ring-width)] [&:focus-visible]:outline-solid [&:focus-visible]:outline-[color:var(--ui-color-ring)] [&:focus-visible]:outline-offset-[2px]"
        :class="{ 'report-channel-tabs__tab--active': selected === tab }"
        type="button"
        role="tab"
        :aria-selected="selected === tab"
        :aria-label="label(tab)"
        :title="label(tab)"
        :tabindex="selected === tab ? 0 : -1"
        @click="selected = tab"
        @keydown.down.prevent="move(index, 1)"
        @keydown.right.prevent="move(index, 1)"
        @keydown.up.prevent="move(index, -1)"
        @keydown.left.prevent="move(index, -1)"
      >
        <Icon
          v-if="isOverview(tab)"
          :class="label(selected) === label(tab) ? 'text-primary' : 'text-primary/40'"
          name="layer-group"
          weight="light"
          :size="22"
        />
        <Icon
          v-else-if="tab === 'Other'"
          :class="label(selected) === label(tab) ? 'text-primary' : 'text-primary/40'"
          name="network-wired"
          weight="light"
          :size="22"
        />
        <PlatformLogo v-else v-bind="logoProps(tab)" :size="22" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.report-channel-tabs .report-channel-tabs__tab--active {
  color: var(--ui-color-primary);
  background-color: var(--ui-color-surface);
  box-shadow: var(--ui-shadow-input);
}
.report-channel-tabs .report-channel-tabs__tab :deep(.ui-platform-logo__text) {
  max-width: 2.25rem;
  overflow: hidden;
  color: currentcolor;
  font-size: 0.625rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-channel-tabs :deep(.report-channel-tabs__overview) {
  max-width: 2.25rem;
  overflow: hidden;
  font-size: 0.625rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
